import React, { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { WishItem } from '@/utils/storage';

interface AddWishListItemProps {
  onAdd: (item: Omit<WishItem, 'id' | 'dateAdded'>) => void;
  onCancel: () => void;
  walletEnabled: boolean;
}

const AddWishListItem: React.FC<AddWishListItemProps> = ({
  onAdd,
  onCancel,
  walletEnabled
}) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    cost: '',
    imageUrl: '',
    purchaseLink: '',
    progress: 0,
    notes: '',
    achieved: false
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    const newErrors: Record<string, string> = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    
    if (!formData.cost || isNaN(Number(formData.cost)) || Number(formData.cost) <= 0) {
      newErrors.cost = 'Please enter a valid cost greater than 0';
    }
    
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    // Create the wish item
    const wishItem: Omit<WishItem, 'id' | 'dateAdded'> = {
      title: formData.title.trim(),
      description: formData.description.trim() || undefined,
      cost: Number(formData.cost),
      imageUrl: formData.imageUrl.trim() || undefined,
      purchaseLink: formData.purchaseLink.trim() || undefined,
      progress: formData.progress,
      notes: formData.notes.trim() || undefined,
      achieved: false,
      achievedDate: undefined
    };
    
    onAdd(wishItem);
  };

  const handleInputChange = (field: string, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const currency = walletEnabled ? '$' : 'stars';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Plus className="h-5 w-5 text-primary" />
            Add New Wish List Item
          </div>
          <Button variant="ghost" size="sm" onClick={onCancel}>
            <X className="h-4 w-4" />
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Title */}
            <div className="md:col-span-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder="What do you want to save up for?"
                className={errors.title ? 'border-red-300' : ''}
              />
              {errors.title && (
                <p className="text-red-500 text-sm mt-1">{errors.title}</p>
              )}
            </div>

            {/* Cost */}
            <div>
              <Label htmlFor="cost">Cost ({currency}) *</Label>
              <Input
                id="cost"
                type="number"
                min="0.01"
                step="0.01"
                value={formData.cost}
                onChange={(e) => handleInputChange('cost', e.target.value)}
                placeholder={walletEnabled ? "10.00" : "50"}
                className={errors.cost ? 'border-red-300' : ''}
              />
              {errors.cost && (
                <p className="text-red-500 text-sm mt-1">{errors.cost}</p>
              )}
            </div>

            {/* Purchase Link */}
            <div>
              <Label htmlFor="purchaseLink">Purchase Link</Label>
              <Input
                id="purchaseLink"
                type="url"
                value={formData.purchaseLink}
                onChange={(e) => handleInputChange('purchaseLink', e.target.value)}
                placeholder="https://example.com/product"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Where can this item be purchased?
              </p>
            </div>

            {/* Image URL */}
            <div>
              <Label htmlFor="imageUrl">Image URL</Label>
              <Input
                id="imageUrl"
                type="url"
                value={formData.imageUrl}
                onChange={(e) => handleInputChange('imageUrl', e.target.value)}
                placeholder="https://example.com/image.jpg"
              />
            </div>

            {/* Description */}
            <div className="md:col-span-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Describe what this item is and why you want it..."
                rows={3}
              />
            </div>

            {/* Notes */}
            <div className="md:col-span-2">
              <Label htmlFor="notes">Personal Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                placeholder="Any additional thoughts or plans..."
                rows={2}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 pt-4">
            <Button type="submit" className="flex-1">
              <Plus className="mr-2 h-4 w-4" />
              Add to Wish List
            </Button>
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default AddWishListItem;